import React, {useState, useMemo} from 'react';
import Modal from './Modal';
import {InformationCircleIcon} from '@heroicons/react/24/outline';

/**
 * CalculatePaymentsModal - Modal for selecting resources to calculate payments for
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is visible
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onCalculate - Callback when user confirms calculation
 *                                   Receives: (resourceIds: string[], ignoreOutstanding: boolean)
 * @param {boolean} props.isCalculating - Whether calculation is in progress
 * @param {Array<Object>} props.resources - List of available resources
 *                                 Each resource should have: id, name
 */
export default function CalculatePaymentsModal({open, onClose, onCalculate, isCalculating, resources = []}) {
  const [selectedResourceIds, setSelectedResourceIds] = useState([]);
  const [ignoreOutstanding, setIgnoreOutstanding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Helper to get resource ID (supports both formats: id and resourceId)
  const getResourceId = (resource) => resource.id || resource.resourceId;

  // Helper to get resource name (supports both formats: name and resourceName)
  const getResourceName = (resource) => resource.name || resource.resourceName || 'Unnamed Resource';

  // Helper to get resource active status
  const getResourceActive = (resource) => {
    // Check both possible fields: is_active and isActive
    const active = resource.is_active ?? resource.isActive ?? true;
    return active;
  };

  // Separate resources into active and inactive
  const { activeResources } = useMemo(() => {
    const active = [];
    const inactive = [];
    resources.forEach(resource => {
      if (getResourceActive(resource)) {
        active.push(resource);
      } else {
        inactive.push(resource);
      }
    });
    return { activeResources: active, inactiveResources: inactive };
  }, [resources]);

  // Filter active resources based on search query
  const filteredActiveResources = useMemo(() => {
    if (!searchQuery.trim()) {
      return activeResources;
    }
    const query = searchQuery.toLowerCase();
    return activeResources.filter(resource => {
      const name = resource.name || resource.resourceName || '';
      const id = resource.id || resource.resourceId || '';
      return name.toLowerCase().includes(query) || id.toLowerCase().includes(query);
    });
  }, [activeResources, searchQuery]);

  // Toggle resource selection
  const toggleResource = (resourceId) => {
    setSelectedResourceIds(prev => {
      if (prev.includes(resourceId)) {
        return prev.filter(id => id !== resourceId);
      } else {
        return [...prev, resourceId];
      }
    });
  };

  // Select all filtered active resources
  const selectAll = () => {
    setSelectedResourceIds(filteredActiveResources.map(r => getResourceId(r)));
  };

  // Deselect all resources
  const deselectAll = () => {
    setSelectedResourceIds([]);
  };

  // Check if all filtered active resources are selected
  const allSelected = filteredActiveResources.length > 0 &&
    filteredActiveResources.every(r => selectedResourceIds.includes(getResourceId(r)));

  // Handle calculate button click
  const handleCalculate = () => {
    if (selectedResourceIds.length === 0) {
      return;
    }

    // Only send selected resource IDs (backend will validate they're active)
    onCalculate(selectedResourceIds, ignoreOutstanding);
  };

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setSelectedResourceIds([]);
      setSearchQuery('');
      setIgnoreOutstanding(false);
    }
  }, [open]);

  return open ? (
    <Modal
      onClose={onClose}
      title="Calculate Payments"
      maxWidthClass="max-w-lg"
      footer={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isCalculating}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCalculate}
            disabled={isCalculating || selectedResourceIds.length === 0}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isCalculating ? (
              <>
                <svg className="animate-spin h-4 w-4 inline mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Calculating...
              </>
            ) : (
              `Calculate (${selectedResourceIds.length})`
            )}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Select the resources to calculate incentives payments for:
        </p>
        {/* Informational note about inactive resources */}
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <InformationCircleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 dark:text-amber-200">
            Only <strong className="font-semibold">Active</strong> resources can have payments calculated. Inactive resources are disabled.
          </p>
        </div>

        {/* Search input */}
        <div>
          <input
            type="text"
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={isCalculating}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Selection controls */}
        {filteredActiveResources.length > 0 && (
          <div className="flex items-center justify-between text-sm flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={selectAll}
                disabled={isCalculating || allSelected}
                className="px-2 py-1 rounded text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Select All
              </button>
              <span className="text-gray-400 dark:text-gray-600">|</span>
              <button
                type="button"
                onClick={deselectAll}
                disabled={isCalculating || selectedResourceIds.length === 0}
                className="px-2 py-1 rounded text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Deselect All
              </button>
            </div>
            <span className="text-gray-600 dark:text-gray-400 font-medium">
              {selectedResourceIds.length} of {filteredActiveResources.length} selected
            </span>
          </div>
        )}

        {/* Resource list */}
        {activeResources.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg">
            No active resources available.
          </div>
        ) : filteredActiveResources.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg">
            No active resources match your search.
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto border border-gray-200/70 dark:border-gray-600/70 rounded-lg bg-surface dark:bg-gray-870">
            {filteredActiveResources.map((resource) => {
              const resourceId = getResourceId(resource);
              const isSelected = selectedResourceIds.includes(resourceId);
              const resourceName = getResourceName(resource);
              const isActive = getResourceActive(resource);
              const isDisabled = isCalculating || !isActive;

              return (
                <div
                  key={resourceId}
                  className={`
                    flex items-center gap-3 px-4 py-3 transition-colors
                    ${isSelected
                      ? 'bg-brand-50 dark:bg-brand-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }
                    ${!isActive ? 'opacity-60' : ''}
                  `}
                >
                  <label
                    className={`flex items-center gap-3 cursor-pointer flex-1 ${!isActive ? 'cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleResource(resourceId)}
                      disabled={isDisabled}
                      className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className={`text-sm font-medium truncate ${isActive ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
                          {isActive ? (
                            <span className="inline-flex items-center gap-1">
                              {resourceName}
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                Active
                              </span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              {resourceName}
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400">
                                Inactive
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                      {resourceId && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {resourceId}
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              );
            })}
          </div>
        )}

        {/* Ignore outstanding toggle */}
        <div className="pt-4 border-t border-gray-200/70 dark:border-gray-600/70">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={ignoreOutstanding}
              onChange={(e) => setIgnoreOutstanding(e.target.checked)}
              disabled={isCalculating}
              className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Ignore outstanding payments
                </span>
                <div className="group relative">
                  <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-500 cursor-help" />
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    When enabled, only calculates payments for the current month without considering any outstanding or pending payment amounts.
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Calculate only current month's payments
              </p>
            </div>
          </label>
        </div>
      </div>
    </Modal>
  ) : null;
}
